import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/context/ToastContext';
import { getHipaaBody, ACTIVE_HIPAA_VERSION, getAttestationText } from '../legal/hipaa';
import { renderTemplate } from '../utils/renderTemplate';
import { generateHipaaPdf } from '../utils/generateHipaaPdf';
import SignaturePad from '../components/SignaturePad';
import ConfirmDialog from '../components/ConfirmDialog';
import GlobalLoader from '@/components/context/GlobalLoader';
import type { FamilyMember, SignerRelationship } from '../types/family';
import type { Profile } from '../types/profile';
import { isMinorForHipaa } from '../utils/stateConsentAges';
import { ArrowLeft, Smartphone, CheckCircle } from 'lucide-react';
import '@styles/pages/hipaa.css';
import '@styles/pages/family.css';

export default function FamilyHipaa() {
  const { familyMemberId } = useParams<{ familyMemberId: string }>();
  const [searchParams] = useSearchParams();
  const isHandoff = searchParams.get('handoff') === 'true';
  const returnTo = searchParams.get('returnTo') || '/family-members';

  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, loading: authLoading } = useAuth();

  const [familyMember, setFamilyMember] = useState<FamilyMember | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [attestation, setAttestation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHandBack, setShowHandBack] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const today = new Date().toLocaleDateString('en-US');

  // Track whether the user has started filling the form
  const hasUnsavedWork = accepted || attestation || !!signatureData;

  // Warn on browser/tab close if form is partially filled
  useEffect(() => {
    if (!hasUnsavedWork || showHandBack) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedWork, showHandBack]);

  /* ---------------------------
     LOAD DATA
  ---------------------------- */
  useEffect(() => {
    if (authLoading || !user || !familyMemberId) return;

    const load = async () => {
      // Check if already signed
      const { data: existing } = await supabase
        .from('hipaa_consents')
        .select('id')
        .eq('user_id', user.id)
        .eq('family_member_id', familyMemberId)
        .limit(1)
        .maybeSingle();

      if (existing) {
        showToast('HIPAA authorization already signed for this family member.');
        navigate(returnTo);
        return;
      }

      // Load family member
      const { data: memberData } = await supabase
        .from('family_members')
        .select('*')
        .eq('id', familyMemberId)
        .eq('user_id', user.id)
        .single();

      if (!memberData) {
        navigate('/family-members');
        return;
      }

      // Load parent profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      setFamilyMember(memberData);
      setProfile(profileData);
      setLoading(false);
    };

    load();
  }, [authLoading, user, familyMemberId, navigate, showToast, returnTo]);

  if (loading) return <GlobalLoader message="Loading authorization..." />;
  if (!familyMember || !profile) return null;

  /* ---------------------------
     DETERMINE CONTEXT
  ---------------------------- */
  const isChild = familyMember.relationship === 'child';
  const isSpouse = familyMember.relationship === 'spouse';
  const patientIsMinor = isMinorForHipaa(familyMember.date_of_birth);
  const parentName = `${profile.first_name} ${profile.last_name}`;
  const patientName = `${familyMember.first_name} ${familyMember.last_name}`;

  // Adult children (18+) sign for themselves, just like spouses
  const adultChild = isChild && !patientIsMinor;
  const needsSelfSign = isSpouse || adultChild;

  let signerRelationship: SignerRelationship;
  if (isChild && patientIsMinor) {
    // Minor child → parent signs
    signerRelationship = 'parent';
  } else if (needsSelfSign && isHandoff) {
    // Spouse or adult child in handoff mode → they sign for themselves
    signerRelationship = isSpouse ? 'spouse' : 'self';
  } else {
    signerRelationship = 'self';
  }

  // If this person needs to sign themselves but we're NOT in handoff mode,
  // show a handoff prompt instead of the signing form
  if (needsSelfSign && !isHandoff) {
    return (
      <div className="hipaa-container">
        <header className="auth-header">
          <div className="logo" aria-label="Mediloop">
            <img
              src="/images/logos/tail-logo.png"
              alt="Mediloop"
              className="logo-img"
              width={36}
              height={36}
            />
          </div>
        </header>

        <main className="hipaa-card">
          <div className="hipaa-eyebrow">  Parent / guardian signature  </div>
          <h1>HIPAA <span className="italic">Authorization.</span></h1>

          <div className="handoff-banner">
            <Smartphone size={18} className="handoff-banner-icon" />
            <div className="handoff-banner-text">
              <strong>{patientName} needs to sign this themselves.</strong>
              {isSpouse
                ? ' As your spouse, they must authorize their own HIPAA release.'
                : ' Since they\'re 18 or older, they must authorize their own HIPAA release.'}
            </div>
          </div>

          <p className="subtitle">
            Hand your device to {familyMember.first_name} so they can review and
            sign. It only takes about 30 seconds.
          </p>

          <div className="hipaa-content">
            <button
              className="primary"
              onClick={() => {
                const params = new URLSearchParams();
                params.set('handoff', 'true');
                if (returnTo !== '/family-members') params.set('returnTo', returnTo);
                navigate(
                  `/hipaa/family/${familyMember.id}?${params.toString()}`,
                  { replace: true }
                );
              }}
            >
              Ready, hand off device
            </button>

            <button
              type="button"
              className="hipaa-back"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={16} />
              Go Back
            </button>
          </div>
        </main>
      </div>
    );
  }

  /* ---------------------------
     CONSENT TEXT
  ---------------------------- */
  const templateBody = getHipaaBody(signerRelationship);

  const templateVars: Record<string, string> = {
    today,
    signature: '[Signed electronically]',
  };

  if (signerRelationship === 'parent') {
    templateVars.patient_name = patientName;
    templateVars.patient_dob = familyMember.date_of_birth;
    templateVars.parent_name = parentName;
    templateVars.printed_name = parentName;
  } else if (signerRelationship === 'spouse') {
    templateVars.full_name = patientName;
    templateVars.date_of_birth = familyMember.date_of_birth;
    templateVars.printed_name = patientName;
    templateVars.account_holder_name = parentName;
  } else {
    templateVars.full_name = patientName;
    templateVars.date_of_birth = familyMember.date_of_birth;
    templateVars.printed_name = patientName;
  }

  const consentText = renderTemplate(templateBody, templateVars);

  /* ---------------------------
     SUBMIT
  ---------------------------- */
  const handleSubmit = async () => {
    if (!accepted) {
      setError('You must accept the HIPAA Release Authorization');
      return;
    }
    if (!attestation) {
      setError('You must confirm the legal attestation');
      return;
    }
    if (!signatureData) {
      setError('Please draw your signature above');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.refreshSession();

      if (sessionError || !sessionData.session) {
        setError('Your session has expired. Please log in again.');
        setSubmitting(false);
        return;
      }

      const userId = sessionData.session.user.id;
      const signerName =
        signerRelationship === 'parent' ? parentName : patientName;

      // Generate PDF
      const pdfBlob = generateHipaaPdf({
        consentText,
        signatureDataUrl: signatureData,
        fullName: signerName,
        dateOfBirth: familyMember.date_of_birth,
        dateSigned: today,
        consentVersion: ACTIVE_HIPAA_VERSION,
        signerRelationship,
        patientName: signerRelationship === 'parent' ? patientName : undefined,
        parentName: signerRelationship === 'parent' ? parentName : undefined,
        attestationChecked: true,
      });

      // Upload PDF
      const fileName = `${userId}/${ACTIVE_HIPAA_VERSION}-family-${familyMember.id}-${Date.now()}.pdf`;

      const { error: uploadError } = await supabase.storage
        .from('hipaa-consents')
        .upload(fileName, pdfBlob, { contentType: 'application/pdf' });

      if (uploadError) {
        setError(uploadError.message);
        setSubmitting(false);
        return;
      }

      // Save consent record
      const { error: insertError } = await supabase
        .from('hipaa_consents')
        .insert({
          user_id: userId,
          family_member_id: familyMember.id,
          consent_version: ACTIVE_HIPAA_VERSION,
          consent_text: consentText,
          signed_name: signerName,
          signature_method: 'drawn',
          signed_pdf_path: fileName,
          user_agent: navigator.userAgent,
          attestation_checked: true,
          signer_relationship: signerRelationship,
        });

      if (insertError) {
        setError(insertError.message);
        setSubmitting(false);
        return;
      }

      if (isHandoff) {
        // Show "hand device back" screen before navigating
        setShowHandBack(true);
      } else {
        showToast(`HIPAA authorization signed for ${patientName}!`);
        navigate(returnTo);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = accepted && attestation && !!signatureData;

  /* ---------------------------
     UI, HAND BACK SCREEN (after handoff signing)
  ---------------------------- */
  if (showHandBack) {
    return (
      <div className="hipaa-container">
        <header className="auth-header">
          <div className="logo" aria-label="Mediloop">
            <img
              src="/images/logos/tail-logo.png"
              alt="Mediloop"
              className="logo-img"
              width={36}
              height={36}
            />
          </div>
        </header>

        <main className="hipaa-card handback-card">
          <div className="handback-icon-wrap">
            <CheckCircle size={36} />
          </div>
          <h1 className="handback-heading">You're all set!</h1>
          <p className="handback-body">
            Thanks, {familyMember.first_name}. Your HIPAA authorization has been
            signed and saved.
          </p>

          <div className="handback-nudge handback-nudge-mobile">
            <Smartphone size={18} />
            <span>Please hand the device back to <strong>{parentName}</strong></span>
          </div>

          <div className="handback-nudge handback-nudge-desktop">
            <span><strong>{parentName}</strong> can now continue uploading the bill</span>
          </div>

          <button
            className="handback-continue"
            onClick={() => {
              showToast(`HIPAA authorization signed for ${patientName}!`);
              navigate(returnTo);
            }}
          >
            Continue
          </button>
        </main>
      </div>
    );
  }

  /* ---------------------------
     UI, SIGNING FORM
  ---------------------------- */
  return (
    <div className="hipaa-container">
      <header className="auth-header">
        <div className="logo" aria-label="Mediloop">
          <img
            src="/images/logos/tail-logo.png"
            alt="Mediloop"
            className="logo-img"
            width={36}
            height={36}
          />
        </div>
      </header>

      <main className="hipaa-card">
        <div className="hipaa-eyebrow">  Parent / guardian signature  </div>
        <h1>HIPAA Release <span className="italic">Authorization.</span></h1>

        {/* Context banner */}
        {isChild && patientIsMinor && (
          <div className="handoff-banner">
            <Smartphone size={18} className="handoff-banner-icon" />
            <div className="handoff-banner-text">
              <strong>Signing as parent/guardian</strong> for {patientName} (minor patient)
            </div>
          </div>
        )}

        {needsSelfSign && isHandoff && (
          <div className="handoff-banner">
            <Smartphone size={18} className="handoff-banner-icon" />
            <div className="handoff-banner-text">
              <strong>{patientName}, you're signing for yourself.</strong> Please
              review and sign below.
            </div>
          </div>
        )}

        <p className="subtitle">
          {isChild && patientIsMinor
            ? `Review and sign the authorization for your child, ${familyMember.first_name}.`
            : needsSelfSign && isHandoff
            ? `${familyMember.first_name}, please review and sign the authorization below.`
            : 'Please review and sign the authorization below.'}
        </p>

        <div className="hipaa-content">
          <pre className="hipaa-box">{consentText}</pre>

          {/* Acceptance checkbox */}
          <label className="checkbox">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span>
              I have read and agree to the HIPAA Release Authorization
            </span>
          </label>

          {/* Legal attestation checkbox */}
          <label className="checkbox attestation-checkbox">
            <input
              type="checkbox"
              checked={attestation}
              onChange={(e) => setAttestation(e.target.checked)}
            />
            <span className="attestation-text">
              <strong>Legal Attestation:</strong> {getAttestationText(signerRelationship)}
            </span>
          </label>

          <SignaturePad onSignatureChange={setSignatureData} />

          {error && <p className="form-error">{error}</p>}

          <button
            className="primary"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
          >
            {submitting ? 'Saving…' : 'Sign & Continue'}
          </button>

          <button
            type="button"
            className="hipaa-back"
            onClick={() => {
              if (hasUnsavedWork) {
                setShowLeaveConfirm(true);
              } else {
                navigate(returnTo);
              }
            }}
            disabled={submitting}
          >
            <ArrowLeft size={16} />
            {isHandoff
              ? 'Not now, I\'ll do this later'
              : returnTo === '/add-bill'
              ? 'Back to Bill Upload'
              : 'Back to Family Members'}
          </button>
        </div>

        <div className="trust-row">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>Your information is encrypted and HIPAA-protected</span>
        </div>
      </main>

      <ConfirmDialog
        open={showLeaveConfirm}
        title="Leave without signing?"
        message="You've started filling out this form. If you leave now, your progress will be lost."
        confirmLabel="Leave"
        cancelLabel="Stay"
        onConfirm={() => navigate(returnTo)}
        onCancel={() => setShowLeaveConfirm(false)}
        variant="danger"
      />
    </div>
  );
}
